import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase, Project } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import AlertDialog from '../components/AlertDialog';
import { getCacheData, setCacheData } from '../utils/dataCache';

const Dashboard = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false); // ✨ بدون loading في البداية
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  
  // Dialog states
  const [showInsufficientCreditsDialog, setShowInsufficientCreditsDialog] = useState(false);
  const [insufficientCreditsMessage, setInsufficientCreditsMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState<Project | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    fetchProjects();
  }, [user, navigate]);

  const fetchProjects = async (showLoadingIndicator: boolean = false) => {
    try {
      // 📦 محاولة تحميل البيانات من الـ cache أولاً
      const cacheKey = `projects_${user?.id}`;
      const cachedProjects = getCacheData<Project[]>(cacheKey);
      
      if (cachedProjects && cachedProjects.length > 0) {
        console.log('✅ Loaded projects from cache');
        setProjects(cachedProjects);
        // تحديث البيانات في الخلفية دون إظهار loading
        showLoadingIndicator = false;
      } else {
        // 📥 أول مرة، إظهار loading
        console.log('📥 Loading projects for the first time...');
        showLoadingIndicator = true;
      }
      
      if (showLoadingIndicator) {
        setLoading(true);
      }

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user?.id) // 🔒 Only show user's own projects
        .order('updated_at', { ascending: false });

      if (error) throw error;
      
      const freshProjects = data || [];
      setProjects(freshProjects);
      
      // 💾 حفظ في الـ cache
      setCacheData(cacheKey, freshProjects);
      
    } catch (err: any) {
      setError(t('pages.dashboard.loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = async () => {
    // Check credits first
    try {
      const { data: creditsValue, error: creditsError } = await supabase
        .rpc('get_user_available_credits', { p_user_id: user?.id });

      console.log('🔍 Dashboard - Create Project Credits:', { creditsValue, creditsError });

      const remaining = creditsValue || 0;
      if (remaining < 25) {
        setInsufficientCreditsMessage(
          `${t('pages.dashboard.insufficientCredits.createCost')}\n` +
          `${t('pages.dashboard.insufficientCredits.remaining', { credits: remaining })}\n\n` +
          `${t('pages.dashboard.insufficientCredits.buyMore')}`
        );
        setShowInsufficientCreditsDialog(true);
        return;
      }
      
      navigate('/create-project');
    } catch (err) {
      console.error('Error checking credits:', err);
      navigate('/create-project');
    }
  };

  const handleOpenProject = (projectId: string) => {
    navigate(`/editor?project=${projectId}`);
  };

  const handleDeleteProject = (projectId: string) => {
    setProjectToDelete(projectId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectToDelete);

      if (error) throw error;
      
      const updatedProjects = projects.filter(p => p.id !== projectToDelete);
      setProjects(updatedProjects);
      
      // 💾 تحديث الـ cache
      const cacheKey = `projects_${user?.id}`;
      setCacheData(cacheKey, updatedProjects);
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleDuplicateProject = (project: Project) => {
    setProjectToDuplicate(project);
    setShowDuplicateConfirm(true);
  };

  const confirmDuplicate = async () => {
    if (!projectToDuplicate) return;

    // Check credits first from new FIFO system
    try {
      const { data: creditsData, error: creditsError } = await supabase.rpc(
        'get_user_available_credits',
        { p_user_id: user?.id }
      );

      const remaining = creditsData || 0;
      console.log('🔍 Credits check (duplicate):', { remaining, userId: user?.id });

      if (remaining < 25) {
        setInsufficientCreditsMessage(
          `${t('pages.dashboard.insufficientCredits.duplicateCost')}\n` +
          `${t('pages.dashboard.insufficientCredits.remaining', { credits: remaining })}\n\n` +
          `${t('pages.dashboard.insufficientCredits.buyMore')}`
        );
        setShowInsufficientCreditsDialog(true);
        return;
      }

      // Create duplicate with new name
      const project = projectToDuplicate;
      const newName = `${project.name}-copy-${Date.now()}`;
      const { data: newProject, error } = await supabase
        .from('projects')
        .insert({
          user_id: user?.id,
          name: newName,
          description: project.description,
          editor_data: project.editor_data,
          html_template: project.html_template,
        })
        .select()
        .single();

      if (error) throw error;

      // Deduct 25 credits using FIFO
      const { error: deductError } = await supabase.rpc('use_credits_fifo', {
        p_user_id: user?.id,
        p_amount: 25,
        p_reason: `${t('pages.dashboard.duplicate.title')}: ${project.name}`
      });

      if (deductError) {
        console.error('Failed to deduct credits:', deductError);
      }

      // Refresh projects list and update cache
      await fetchProjects(false); // تحديث في الخلفية بدون loading
    } catch (err: any) {
      alert('× ' + t('pages.dashboard.duplicate.error') + ': ' + err.message);
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('pages.dashboard.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />
      
      {/* Sub-header with Create Button */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">{t('pages.dashboard.title')}</h2>
            <button
              onClick={handleCreateNew}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">{t('pages.dashboard.newProject')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-24 h-24 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-white mb-2">
              {t('pages.dashboard.empty.title')}
            </h3>
            <p className="text-gray-400 mb-6">
              {t('pages.dashboard.empty.desc')}
            </p>
            <button
              onClick={handleCreateNew}
              className="bg-[#1e48e0] hover:bg-[#163aa8] text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('pages.dashboard.empty.button')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="rounded-xl overflow-hidden hover:shadow-xl hover:shadow-[#1e48e0]/20 transition-all group relative"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {/* Cover/Thumbnail - مربع (1:1) */}
                <div 
                  className="flex items-center justify-center relative overflow-hidden"
                  style={{ 
                    aspectRatio: '1/1',  // مربع
                    width: '100%',
                    background: '#000'
                  }}
                >
                  {(project as any).cover || project.thumbnail_url ? (
                    <img 
                      src={(project as any).cover || project.thumbnail_url} 
                      alt={project.name} 
                      className="w-full h-full"
                      style={{ objectFit: 'contain' }}
                    />
                  ) : (
                    <svg className="w-16 h-16 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleOpenProject(project.id)}
                      className="bg-[#1e48e0] hover:bg-[#163aa8] text-white p-2 rounded-lg transition-colors"
                      title={t('pages.dashboard.project.open')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDuplicateProject(project)}
                      className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors"
                      title={t('pages.dashboard.project.duplicate')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                      title={t('pages.dashboard.project.delete')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Info - Liquid Glass */}
                <div 
                  className="p-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    borderTop: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <h3 className="text-lg font-semibold text-white mb-1 truncate">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {new Date(project.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <span>
                      {project.editor_data.elements.length} {t('pages.dashboard.project.elements')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Insufficient Credits Dialog */}
      <AlertDialog
        isOpen={showInsufficientCreditsDialog}
        onClose={() => setShowInsufficientCreditsDialog(false)}
        title={t('pages.dashboard.insufficientCredits.title')}
        message={insufficientCreditsMessage}
        type="error"
        icon={
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        }
      />

      {/* Delete Project Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProjectToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('pages.dashboard.delete.title')}
        message={t('pages.dashboard.delete.message')}
        confirmText={t('pages.dashboard.delete.confirm')}
        cancelText={t('common.cancel')}
        type="danger"
        icon={
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
        }
      />

      {/* Duplicate Project Confirmation */}
      <ConfirmDialog
        isOpen={showDuplicateConfirm}
        onClose={() => {
          setShowDuplicateConfirm(false);
          setProjectToDuplicate(null);
        }}
        onConfirm={confirmDuplicate}
        title={t('pages.dashboard.duplicate.title')}
        message={t('pages.dashboard.duplicate.message')}
        confirmText={t('pages.dashboard.duplicate.confirm')}
        cancelText={t('pages.dashboard.duplicate.cancel')}
        type="warning"
        icon={
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
        }
      />
    </div>
  );
};

export default Dashboard;
